/**
 * A ponte entre o laço de frame (60 Hz) e o React (≤ 10 Hz).
 *
 * O invariante 3 vive AQUI: `tick` é chamado por frame, e só publica na cadência.
 * Sem esse limite, um HUD de depuração passaria a ser a coisa mais cara da tela —
 * React reconciliando 60 vezes por segundo para mostrar número que o olho NEVER
 * lê nessa velocidade.
 *
 * `collect` é preguiçoso de propósito: `FrameSampler.compute()` ordena a janela
 * inteira, e chamar isso por frame seria exatamente o custo que o HUD deveria medir.
 */
import { hudRows, type HudInput, type HudRow } from './hud-model.js';

/**
 * Cadência máxima de publicação. NEVER sai de `PERF.yaml`: não é orçamento de
 * aparelho, é regra de arquitetura do cliente (invariante 3, ADR-0003).
 */
const PUBLISH_HZ = 10;
const MIN_INTERVAL_MS = 1000 / PUBLISH_HZ;

export interface HudSnapshot {
  readonly rows: readonly HudRow[];
  readonly publishedAtMs: number;
}

const EMPTY: HudSnapshot = { rows: [], publishedAtMs: -1 };

export class HudStore {
  private snapshot: HudSnapshot = EMPTY;
  private lastPublishMs: number | undefined;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly collect: () => HudInput) {}

  /**
   * Caminho quente: uma vez por frame. Publica no primeiro e depois na cadência.
   * O primeiro é imediato para o HUD NEVER nascer vazio na tela.
   */
  tick(nowMs: number): void {
    const last = this.lastPublishMs;
    if (last !== undefined && nowMs - last < MIN_INTERVAL_MS) return;
    this.lastPublishMs = nowMs;
    this.snapshot = { rows: hudRows(this.collect()), publishedAtMs: nowMs };
    for (const listener of this.listeners) listener();
  }

  /**
   * Arrow de propósito: `useSyncExternalStore` compara a referência da função e
   * reassinaria a cada render se isto fosse método.
   */
  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * ALWAYS devolve a MESMA referência entre publicações: `useSyncExternalStore`
   * entra em laço infinito se o snapshot mudar de identidade a cada leitura.
   */
  readonly getSnapshot = (): HudSnapshot => this.snapshot;
}
